import React, { useState } from "react";
import type { BryntumTaskBoard } from "@bryntum/taskboard-react-thin";
import type { ProjectModel } from "@bryntum/taskboard-thin";
import { getDetachedDay } from "@laborchart-modules/common/dist/datetime";
import { Button, DateSelect, useI18nContext } from "@procore/core-react";
import { ChevronLeft, ChevronRight } from "@procore/core-icons";

// Utility functions for localStorage
export function saveBoardsSelectedDate(date: Date) {
   localStorage.setItem("boards-selected-date", date.toISOString());
}

export function loadBoardsSelectedDate(): Date {
   const saved = localStorage.getItem("boards-selected-date");
   if (saved) {
      const date = new Date(saved);
      // Check if the date is valid
      if (!isNaN(date.getTime())) {
         return date;
      }
   }
   return new Date();
}

// Exported for testing purposes.
export function onDateChange(
   newDate: Date | null,
   setter: React.Dispatch<Date>,
   taskBoardRef: React.RefObject<BryntumTaskBoard>,
) {
   const newVal = newDate ?? new Date();
   setter(newVal);
   saveBoardsSelectedDate(newVal);
   const projectConfig = taskBoardRef.current?.instance.project as ProjectModel;
   (projectConfig as any).transport.load.params.day_filter = getDetachedDay(newVal);
   // Catching with an empty function to stop console logging an uncaught error.
   // onLoadFail will handle errors.
   projectConfig.load().catch(() => {});
}

export function BoardsDateSelector({
   taskBoardRef,
}: {
   taskBoardRef: React.RefObject<BryntumTaskBoard>;
}) {
   const I18n = useI18nContext();
   const [selectedDate, setSelectedDate] = useState(loadBoardsSelectedDate);

   return (
      <>
         <Button
            variant="secondary"
            icon={<ChevronLeft />}
            onClick={() => {
               const newDate = new Date(selectedDate);
               newDate.setDate(newDate.getDate() - 1);
               onDateChange(newDate, setSelectedDate, taskBoardRef);
            }}
            size="md"
            aria-label={I18n.t("views.company.workforce_planning.boards.previous_day")}
         />
         <DateSelect
            onChange={(newDate) => onDateChange(newDate, setSelectedDate, taskBoardRef)}
            value={selectedDate}
         />
         <Button
            variant="secondary"
            icon={<ChevronRight />}
            onClick={() => {
               const newDate = new Date(selectedDate);
               newDate.setDate(newDate.getDate() + 1);
               onDateChange(newDate, setSelectedDate, taskBoardRef);
            }}
            size="md"
            aria-label={I18n.t("views.company.workforce_planning.boards.next_day")}
         />
      </>
   );
}
